import { Button } from "components/ui/button";
import { Card } from "components/ui/card";
import { Progress } from "components/ui/progress";
import { Spinner } from "components/ui/spinner";
import { cn } from "@/lib/utils";
import { Trash2 } from "lucide-react";
import { FileUploadedProps } from "pages/geoscape-batch/types";
import { FileWithPath } from "react-dropzone";
import { formatBytes } from "utils/commons";

const FileUploaded = ({
  filesData,
  isUploading,
  haveUploaded,
  uploadProgress,
  deleteObjectMutation,
  batchId,
  props,
  s3UploadFailed,
}: FileUploadedProps) => {
  const batchConfigFileUploaded = props.batchConfig.hubInfo?.files.length > 0;

  const file = props.batchConfig?.hubInfo?.files[0];

  const filesSelected = filesData.map((file: FileWithPath, index: number) => {
    if (!file.path) {
      return null;
    }

    return (
      <div key={`files-selected-${index}`}>
        {s3UploadFailed ? (
          <p className="text-destructive text-sm">
            "Please refresh the page and upload the file again. If the error
            persists, please contact support."
          </p>
        ) : (
          <>
            <Card key={file.path} className="flex-row p-2 mt-2">
              <div className="w-full mr-2">
                <p className="text-sm">{file.path}</p>
                {(isUploading || haveUploaded) && file.path ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(file.size)} -{" "}
                      {uploadProgress[file.path].percentComplete === 100
                        ? "COMPLETE"
                        : `about ${
                            uploadProgress[file.path].humanisedTimeRemaining
                          } remaining`}
                    </p>
                    <Progress
                      value={uploadProgress[file.path].percentComplete}
                      className={cn(
                        "h-1 rounded-sm mt-1",
                        uploadProgress[file.path].percentComplete === 100
                          ? "**:data-[slot=progress-indicator]:bg-success"
                          : "**:data-[slot=progress-indicator]:bg-info",
                      )}
                    />
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </p>
                )}
              </div>
            </Card>

            <div className="flex justify-between items-center mt-2">
              <p className="text-muted-foreground text-sm">
                If you navigate away at any time, you will have to reupload
                these files.
              </p>
              <Button
                variant="destructive"
                disabled={isUploading || deleteObjectMutation.isPending}
                onClick={async () => {
                  deleteObjectMutation.mutate(
                    batchId + "-" + filesData[0].path,
                  );
                }}
              >
                <Trash2 />
                {(isUploading || deleteObjectMutation.isPending) && <Spinner />}
                Delete
              </Button>
            </div>
          </>
        )}
      </div>
    );
  });

  const fileLoadedFromProps = () => {
    return (
      <>
        <Card className="flex-row p-2 mt-2">
          <div className="w-full mr-2">
            <p className="text-sm">{file.path}</p>
            <p className="text-xs text-muted-foreground">
              {formatBytes(file.size)} - "COMPLETE"
            </p>
            <Progress
              value={100}
              className="h-1 rounded-sm mt-1 **:data-[slot=progress-indicator]:bg-success"
            />
          </div>
        </Card>

        <div className="flex justify-between items-center mt-2">
          <p className="text-muted-foreground text-sm">
            If you navigate away at any time, you will have to reupload these
            files.
          </p>
          <Button
            variant="destructive"
            disabled={isUploading || deleteObjectMutation.isPending}
            onClick={async () => {
              deleteObjectMutation.mutate(batchId + "-" + file.path);
            }}
          >
            <Trash2 />
            {(isUploading || deleteObjectMutation.isPending) && <Spinner />}
            Delete
          </Button>
        </div>
      </>
    );
  };

  return <>{batchConfigFileUploaded ? fileLoadedFromProps() : filesSelected}</>;
};

export default FileUploaded;
